import {
    FETCH_DATA_INGREDIENTS_REQUEST,
    FETCH_DATA_INGREDIENTS_SUCCESS,
    FETCH_DATA_INGREDIENTS_ERROR,

    GET_MY_INGREDIENTS_LOCAL,
    UPDATE_MY_INGREDIENTS_LOCAL_BY_LANG,
    FETCH_DATA_MY_INGREDIENTS_REQUEST,
    FETCH_DATA_MY_INGREDIENTS_SUCCESS,
    FETCH_DATA_MY_INGREDIENTS_ERROR,
    SORT_DATA_MY_INGREDIENTS,
    PREPEND_MY_INGREDIENTS,
    REMOVE_MY_INGREDIENTS,
    
    SET_MY_INGREDIENTS_REQUEST_LOCAL,
    GET_MY_INGREDIENTS_REQUEST_LOCAL,
    UPDATE_MY_INGREDIENTS_REQUEST_LOCAL_BY_LANG,

    FETCH_DATA_FLAVORS_REQUEST,
    FETCH_DATA_FLAVORS_SUCCESS,
    FETCH_DATA_FLAVORS_ERROR,

    REQUEST_INGREDIENT_REQUEST,
    REQUEST_INGREDIENT_SUCCESS,
    REQUEST_INGREDIENT_ERROR,
    REQUEST_INGREDIENT_RESET,

    SET_RECIPE_INGREDIENTS,
    SET_RECIPE_OPTIONS,
    SET_RECIPE_KEYWORD,
    RESET_RECIPE_INGREDIENTS,
    RESET_RECIPE_OPTIONS,
    RESET_RECIPE_KEYWORD,
    RESET_RECIPE,
    FETCH_DATA_HISTORY_REQUEST,
    FETCH_DATA_HISTORY_SUCCESS,
    FETCH_DATA_HISTORY_ERROR,
} from './actions';
import type {
    TOption,
    TIngredient,
    TFravor,
    TFlavor,
    THistory,
} from './types';
import {
    sortBy,
    sortIngredients,
    getMyIngredients,
    updateMyIngredients,
    setMyIngredients,
    setMyIngredientRequest,
    getMyIngredientRequest,
    updateMyIngredientsRequest,
} from './helper';

interface TMyIngredients {
    filter: {
        sort: TOption
    },
    dataType: 'local' | 'server',
    user_id: number,
    lang: string,
    data: TIngredient[],
    localData: TIngredient[],
    loading: boolean,
    error: Error | null,
}

export interface DataState {
    ingredients: {
        data: TIngredient[],
        loading: boolean,
        error: Error | null,
    },
    myIngredients: TMyIngredients,
    myIngredientsRequested: TMyIngredients,
    request: {
        data: TIngredient,
        loading: boolean,
        error: Error | null,
    },
    recipes: {
        ingredients: TIngredient[],
        options: {
            [key: number]: TFravor[]
        },
        keyword: string;
    },
    flavors: {
        data: TFlavor[],
        loading: boolean,
        error: Error | null,
    },
    history: {
        data: THistory[],
        loading: boolean,
        error: Error | null,
        params: {
            page: number,
            page_size: number,
        }
        lastPage: number
    },
}

const initIngredients: DataState['ingredients'] = {
    data: [], // list ingredients
    loading: false,
    error: null,
}

const initMyIngredients: TMyIngredients = {
    filter: {
        sort: sortBy[0]
    }, // filter my ingredients
    dataType: 'local', // list my ingredients from server
    user_id: 0, // list my ingredients from server
    lang: '', // list my ingredients from server
    data: [], // list my ingredients from server
    localData: [], // list my ingredients from local
    loading: false,
    error: null,
}

const initRequest: DataState['request'] = {
    data: {} as TIngredient, // list new ingredient
    loading: false,
    error: null,
}

const initRecipes: DataState['recipes'] = {
    ingredients: [], // ingredients selected
    options: {}, // ingredients selected
    keyword: '', // keyword
}

const initFlavors: DataState['flavors'] = {
    data: [], // list ingredients
    loading: false,
    error: null,
}

const initHistory: DataState['history'] = {
    data: [], // list ingredients
    loading: false,
    error: null,
    params: {
        page: 1,
        page_size: 6,
    },
    lastPage: 0
}

const initialState: DataState = {
    ingredients: initIngredients,

    recipes: initRecipes,
    myIngredients: initMyIngredients,
    myIngredientsRequested: initMyIngredients,
    request: initRequest,
    flavors: initFlavors,
    history: initHistory
};

const authReducer = (
    state: DataState = initialState,
    action: any,
): DataState => {
    switch (action.type) {

        // list all ingredients public
        case FETCH_DATA_INGREDIENTS_REQUEST:
            return {
                ...state,
                ingredients: {
                    ...state.ingredients,
                    loading: true,
                }
            };
        case FETCH_DATA_INGREDIENTS_SUCCESS:
            return {
                ...state,
                ingredients: {
                    ...state.ingredients,
                    loading: false,
                    data: sortIngredients(
                        action.payload,
                        sortBy[2]
                    )
                }
            };
        case FETCH_DATA_INGREDIENTS_ERROR:
            return {
                ...state,
                ingredients: {
                    ...state.ingredients,
                    loading: false,
                    error: action.payload
                }
            };

        // selecting recipe ingredients
        case SET_RECIPE_INGREDIENTS:
            return {
                ...state,
                recipes: {
                    ...state.recipes,
                    ingredients: action.payload
                }
            };
        case RESET_RECIPE_INGREDIENTS:
            return {
                ...state,
                recipes: {
                    ...state.recipes,
                    ingredients: []
                }
            };
        // selecting recipe flavor items
        case SET_RECIPE_OPTIONS:
            return {
                ...state,
                recipes: {
                    ...state.recipes,
                    options: {
                        ...state.recipes.options,
                        [action.payload.user_id]: action.payload.options
                    }
                }
            };
        case RESET_RECIPE_OPTIONS:
            return {
                ...state,
                recipes: {
                    ...state.recipes,
                    options: []
                }
            };
        // selecting recipe keyword
        case SET_RECIPE_KEYWORD:
            return {
                ...state,
                recipes: {
                    ...state.recipes,
                    keyword: action.payload
                }
            };
        case RESET_RECIPE_KEYWORD:
            return {
                ...state,
                recipes: {
                    ...state.recipes,
                    keyword: ''
                }
            };
        // clear all selecting recipe option
        case RESET_RECIPE:
            return {
                ...state,
                recipes: {
                    ...initRecipes,
                    options: state.recipes.options,
                }
            };

        // my ingredients
        case GET_MY_INGREDIENTS_LOCAL: {
            return {
                ...state,
                myIngredients: {
                    ...state.myIngredients,
                    filter: {
                        sort: state.myIngredients.filter.sort || sortBy[0]
                    },
                    dataType: 'local',
                    user_id: action.payload.user_id,
                    localData: sortIngredients(
                        getMyIngredients(
                            action.payload.user_id,
                            action.payload.lang
                        ),
                        state.myIngredients.filter?.sort || sortBy[0]
                    )
                }
            };
        }
        case UPDATE_MY_INGREDIENTS_LOCAL_BY_LANG: {
            const data = sortIngredients(
                updateMyIngredients(
                    state.myIngredients.user_id,
                    action.payload.lang,
                    state.myIngredients.localData,
                    action.payload.data
                ),
                state.myIngredients.filter?.sort || sortBy[0]
            )
            return {
                ...state,
                myIngredients: {
                    ...state.myIngredients,
                    dataType: 'local',
                    lang: action.payload.lang,
                    localData: data,
                    data: data,
                }
            };
        }
        case FETCH_DATA_MY_INGREDIENTS_REQUEST:
            return {
                ...state,
                myIngredients: {
                    ...state.myIngredients,
                    loading: true,
                    dataType: 'server',
                }
            };
        case FETCH_DATA_MY_INGREDIENTS_SUCCESS: {
            const data = sortIngredients(
                updateMyIngredients(
                    state.myIngredients.user_id,
                    state.myIngredients.lang,
                    state.myIngredients.localData,
                    action.payload
                ),
                state.myIngredients.filter?.sort || sortBy[0]
            )
            return {
                ...state,
                myIngredients: {
                    ...state.myIngredients,
                    loading: false,
                    data: data,
                    localData: data,
                }
            };
        }
        case FETCH_DATA_MY_INGREDIENTS_ERROR:
            return {
                ...state,
                myIngredients: {
                    ...state.myIngredients,
                    loading: false,
                    error: action.payload
                }
            };
        case PREPEND_MY_INGREDIENTS: {
            // const isLocal = state.myIngredients.dataType === 'local'
            // const key = (isLocal ? 'localData' : 'data') as keyof DataState['myIngredients']
            const data = sortIngredients(
                [
                    ...action.payload.items,
                    ...state.myIngredients.data as TIngredient[]
                ],
                state.myIngredients.filter.sort
            )
            // if (isLocal) {
                setMyIngredients(action.payload.user_id, action.payload.lang, data)
            // }
            return {
                ...state,
                myIngredients: {
                    ...state.myIngredients,
                    data: data,
                    localData: data,
                }
            };
        }
        case REMOVE_MY_INGREDIENTS: {
            // const isLocal = state.myIngredients.dataType === 'local'
            // const key = (isLocal ? 'localData' : 'data') as keyof DataState['myIngredients']
            const data = [...state.myIngredients.data as TIngredient[]]
                .filter(
                    (e: TIngredient) => !action.payload.ids.includes(e.id)
                )
            setMyIngredients(action.payload.user_id, action.payload.lang, data)
            return {
                ...state,
                myIngredients: {
                    ...state.myIngredients,
                    data: data,
                    localData: data,
                }
            };
        }
        case SORT_DATA_MY_INGREDIENTS:
            return {
                ...state,
                myIngredients: {
                    ...state.myIngredients,
                    filter: {
                        ...state.myIngredients.filter,
                        sort: action.payload,
                    },
                    localData: sortIngredients(
                        state.myIngredients?.localData || [] as TIngredient[],
                        action.payload
                    ),
                    data: sortIngredients(
                        state.myIngredients?.data || [] as TIngredient[],
                        action.payload
                    )
                }
            };

        case SET_MY_INGREDIENTS_REQUEST_LOCAL: {
            setMyIngredientRequest(
                action.payload.user_id,
                action.payload.lang,
                state.myIngredientsRequested.localData,
                action.payload.data
            )
            return {
                ...state,
                myIngredientsRequested: {
                    ...state.myIngredientsRequested,
                    dataType: 'local',
                    user_id: action.payload.user_id,
                    localData: sortIngredients(
                        getMyIngredientRequest(
                            action.payload.user_id,
                            action.payload.lang
                        ),
                        sortBy[0]
                    )
                }
            };
        }

        case GET_MY_INGREDIENTS_REQUEST_LOCAL: {
            return {
                ...state,
                myIngredientsRequested: {
                    ...state.myIngredientsRequested,
                    dataType: 'local',
                    user_id: action.payload.user_id,
                    localData: sortIngredients(
                        getMyIngredientRequest(
                            action.payload.user_id,
                            action.payload.lang
                        ),
                        sortBy[0]
                    )
                }
            };
        }
        case UPDATE_MY_INGREDIENTS_REQUEST_LOCAL_BY_LANG: {
            const data = updateMyIngredientsRequest(
                state.myIngredientsRequested.user_id,
                action.payload.lang,
                state.myIngredientsRequested.localData,
                action.payload.data
            )
            return {
                ...state,
                myIngredientsRequested: {
                    ...state.myIngredientsRequested,
                    dataType: 'local',
                    lang: action.payload.lang,
                    localData: sortIngredients(data, sortBy[0])
                }
            };
        }

        // request new ingradient
        case REQUEST_INGREDIENT_REQUEST:
            return {
                ...state,
                request: {
                    ...state.request,
                    loading: true,
                    data: initRequest.data
                }
            };
        case REQUEST_INGREDIENT_SUCCESS:
            return {
                ...state,
                request: {
                    ...state.request,
                    loading: false,
                    data: action.payload
                }
            };
        case REQUEST_INGREDIENT_ERROR:
            return {
                ...state,
                request: {
                    ...state.request,
                    loading: false,
                    error: action.payload
                }
            };
        case REQUEST_INGREDIENT_RESET:
            return {
                ...state,
                request: initRequest
            };

        // all flavors
        case FETCH_DATA_FLAVORS_REQUEST:
            return {
                ...state,
                flavors: {
                    ...state.flavors,
                    loading: true,
                }
            };
        case FETCH_DATA_FLAVORS_SUCCESS:
            return {
                ...state,
                flavors: {
                    ...state.flavors,
                    loading: false,
                    data: action.payload
                }
            };
        case FETCH_DATA_FLAVORS_ERROR:
            return {
                ...state,
                flavors: {
                    ...state.flavors,
                    loading: false,
                    error: action.payload
                }
            };

        // history
        case FETCH_DATA_HISTORY_REQUEST:
            console.log('FETCH_DATA_HISTORY_REQUEST', action.payload);
            
            return {
                ...state,
                history: {
                    ...state.history,
                    params: action.payload,
                    loading: true,
                }
            };
        case FETCH_DATA_HISTORY_SUCCESS:{
            // let listData = megerArrayList(state.history?.data as [],action.payload.data?.data)
            // if (action.payload?.data?.current_page === 1) {
            //     listData = action.payload.data?.data;
            // }

            console.log('FETCH_DATA_HISTORY_SUCCESS', action.payload);
            
            const { params } : any = state.history;

            const listData = params.page === 1
            ? action.payload.data
            : state.history.data.concat(action.payload.data);
            console.log('listData', listData);
            
            return {
                ...state,
                history: {
                    ...state.history,
                    data: listData,
                    loading: false,
                    error: null,
                    lastPage: action.payload?.last_page
                }
            };
        }
            
        case FETCH_DATA_HISTORY_ERROR:
            return {
                ...state,
                history: {
                    ...state.history,
                    loading: false,
                    error: action.payload
                }
            };
        default:
            return state;
    }
};

export default authReducer
